import { useMemo } from 'react';
import type { Column } from '../../Table/Table';
import { formatNumberWithCommas } from '../../../lib/numbers/formatNumberWithComma';
import { Icon, IconType } from '../../Props/Icon/Icon';
import styled from 'styled-components';
import type { LabelledToggleOption } from '../../Toggle/LabelledToggle/LabelledToggle';
import type { LeaderboardUser, LeaderboardDepartment } from '../../../lib/types/LeaderboardUser';
import { UserRank } from '../components';
import { NamedContentEntryText } from '../../ContentEntryText/NamedContentEntryText';
import { AvatarInitials } from '../../AvatarInitials';
import { LeaderboardTypeParam } from '../../../apiHooks/useLeaderboardQuery';
import { Ellipsis } from '../../utility/Ellipsis';

type LeaderboardWidgetData = {
  columns: Column<LeaderboardUser | LeaderboardDepartment>[];
  scopeFilterLabels: LabelledToggleOption<LeaderboardTypeParam>[];
};

export const useLeaderboardWidgetData = (
  _isManager: boolean,
  selectedFilterScope?: LeaderboardTypeParam,
): LeaderboardWidgetData => {
  return useMemo(
    () => ({
      scopeFilterLabels: [
        {
          label: <NamedContentEntryText container="span" refKey="leaderboard" subKey="myTeam" />,
          value: LeaderboardTypeParam.TEAM,
          selected: selectedFilterScope === LeaderboardTypeParam.TEAM,
        },
        // TODO: Change to all departments filter when this functionality is implemented on BE
        {
          label: <NamedContentEntryText container="span" refKey="leaderboard" subKey="departments" />,
          value: LeaderboardTypeParam.DEPARTMENT,
          selected: selectedFilterScope === LeaderboardTypeParam.DEPARTMENT,
        },
        {
          label: <NamedContentEntryText container="span" refKey="leaderboard" subKey="myCompany" />,
          value: LeaderboardTypeParam.COMPANY,
          selected: selectedFilterScope === LeaderboardTypeParam.COMPANY,
        },
      ],
      columns:
        selectedFilterScope === LeaderboardTypeParam.DEPARTMENT
          ? [
              {
                header: 'Rank',
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return <UserRank compact rank={department.rank} previousRank={department.previousRank} />;
                },
                alignment: 'left',
                width: '56px',
              },
              {
                header: 'Department',
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return (
                    <UserName>
                      <Ellipsis>{department.department}</Ellipsis>
                    </UserName>
                  );
                },
                alignment: 'left',
                width: '1fr',
              },
              {
                header: 'Avg Score',
                valueSelector: (dept: LeaderboardUser | LeaderboardDepartment) => {
                  const department = dept as LeaderboardDepartment;
                  return (
                    <UserPoints>
                      <Icon width={16} height={16} icon={IconType.COIN} />
                      {formatNumberWithCommas(department.averageScore)}
                    </UserPoints>
                  );
                },
                alignment: 'right',
                width: '100px',
              },
            ]
          : [
              {
                header: 'Rank',
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return <UserRank compact rank={userData.rank} previousRank={userData.previousRank} />;
                },
                alignment: 'left',
                width: '56px',
              },
              {
                header: 'Name',
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return (
                    <UserName>
                      {userData.avatar ? (
                        <UserImg src={`data:image/png;base64,${userData.avatar}`} />
                      ) : (
                        <AvatarInitials name={userData.name || ''} id={userData.name ?? ''} size="28px" />
                      )}
                      <Ellipsis>{userData.name}</Ellipsis>
                    </UserName>
                  );
                },
                alignment: 'left',
                width: '1fr',
              },
              {
                header: 'Points',
                valueSelector: (user: LeaderboardUser | LeaderboardDepartment) => {
                  const userData = user as LeaderboardUser;
                  return (
                    <UserPoints>
                      <Icon width={16} height={16} icon={IconType.COIN} />
                      {formatNumberWithCommas(userData.cyberIqPoints)}
                    </UserPoints>
                  );
                },
                alignment: 'right',
                width: '100px',
              },
            ],
    }),
    [selectedFilterScope],
  );
};

const UserPoints = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.strong};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UserImg = styled.img`
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
`;

const UserName = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`;
